/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Flex, LoadingSpinner } from '@motili/ui';
import _ from 'lodash';

import { useAppDispatch, useAppSelector } from 'common/stores/redux/hooks';
import { useWorkOrderPhotoTypesQuery } from 'common/stores/redux/MetaStore/MetaStoreApi';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_STATUS,
    QUOTE_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import Alert from 'common/components/Alert';
import { DocumentRequirementsPhotosUploadDrawer } from 'common/components/document/DocumentRequirementsPhotosDrawer';
import { FileUploadDropZone } from 'common/components/file-upload/FileUploadDropzone';
import { FormElement, FormRow } from 'common/components/form/FormLayout';
import {
    AcceptedFile,
    allowFileUpload,
    FileUploadType,
    getAllowFileUpload,
    getFileUploadErrorMessage,
} from 'common/components/file-upload/FileUploadTypes';

import DocumentRequirementsPhotosUploaded from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import DocumentRequirementsPhotosRequired from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosRequired';

import { useUI } from 'common/context/UIContext';
import Content from 'common/components/layout/Content';
import { isMetaDataLoaded } from 'common/utils/Utils';
import AdminWorkOrderRequirementsChecklists from 'admin/components/work-orders/AdminWorkOrderRequirementsChecklists/AdminWorkOrderRequirementsChecklists';
import { saveChecklist } from 'admin/services/AdminWorkOrdersService';
import { getWorkOrder } from 'contractor/services/ContractorWorkOrdersService';
import { replace } from 'contractor/stores/redux/ContractorWorkOrder';
import * as logger from 'common/utils/Logger';
import { Client } from 'common/types/client';
import LegacyDocumentRequirementsPhotos from './DocumentRequirementsPhotos-Legacy';

export interface Requirement {
    requirementTypeId: string;
    id?: string;
    name?: string;
    description?: string;
    percentMargin?: number;
    photoTypeId: string;
    [key: string]: unknown;
}

export interface Photo {
    id?: string;
    approvalStatusId?: string;
    fileName?: string;
    url?: string;
    typeId?: string;
    description?: string;
    uploadedAt?: string;
    uploadedBy?: string;
    requirement: Requirement;
    [key: string]: unknown;
}

export interface PhotoApproval {
    id?: string;
    photoId: string;
    status: string;
    notes?: string;
    approvedBy?: string;
    approvedAt?: string;
}

export interface CheckIn {
    checkedOutAt?: string | null;
    checkedInUserId: number;
    checkedInAt?: string;
}

export interface Document {
    statusId: string;
    documentTypeId: string;
    checkInsV2: CheckIn[];
    id?: string;
    name?: string;
    client?: Client;
    [key: string]: unknown;
}

export interface AssetType {
    id: string;
    name: string;
    description?: string;
    [key: string]: unknown;
}

interface DocumentRequirementsPhotosProps {
    isLoading: boolean;
    clientRequirements?: Requirement[];
    projectRequirements?: Requirement[];
    photos?: Photo[];
    disabled?: boolean;
    acls?: boolean;
    removeAcls?: boolean;
    handleUploadPhotosOnClick?: (
        file: File,
        typeId: string,
        description: string,
        requiresPullAndClean: boolean
    ) => Promise<void>;
    handleReUploadPhotoOnClick?: (photoId: string) => void;
    handleSavePhotoOnClick?: (photo: Photo) => void;
    handleRemovePhotoOnClick?: (photoId: string) => void;
    handleEditPhotoRequirements?: (requirement: Requirement) => void;
    handleEditUploadedPhotoRequirement?: (
        photo: Photo,
        requirement: Requirement
    ) => void;
    handleGetApprovalHistoryPhotoRequirement?: (photoId: string) => void;
    handleSaveApprovalPhotoRequirement?: (
        photoId: string,
        status: string,
        notes?: string
    ) => void;
    handleBulkApprovalPhotoRequirement?: (
        photoIds: string[],
        status: string,
        notes?: string
    ) => void;
    handleEditApprovalPhotoRequirement?: (
        photoId: string,
        approval: PhotoApproval
    ) => void;
    document?: Document;
    assetTypes?: AssetType[];
    enableNotApplicablePhoto?: boolean;
    showPhotoTypeRequirementsSection?: boolean;
}

const DocumentRequirementsPhotos = ({
    isLoading,
    handleUploadPhotosOnClick,
    handleReUploadPhotoOnClick,
    handleSavePhotoOnClick,
    handleRemovePhotoOnClick,
    handleEditPhotoRequirements,
    handleEditUploadedPhotoRequirement,
    handleGetApprovalHistoryPhotoRequirement,
    handleSaveApprovalPhotoRequirement,
    handleBulkApprovalPhotoRequirement,
    handleEditApprovalPhotoRequirement,
    document,
    clientRequirements = [],
    projectRequirements = [],
    photos = [],
    disabled = false,
    acls = false,
    removeAcls = false,
    assetTypes = [],
    enableNotApplicablePhoto = false,
    showPhotoTypeRequirementsSection = false,
}: DocumentRequirementsPhotosProps) => {
    const { isMotiliAccount, isContractorAccount, token } = useAppSelector(
        state => state.authentication
    );
    const dispatch = useAppDispatch();
    const { isClientEnablePhotoVerification } = useAppSelector(
        state => state.adminWorkOrder
    );

    const accountId = token?.userId;

    const [acceptedFiles, setAcceptedFiles] = useState<AcceptedFile[]>([]);
    const [currentPhotoTypeId, setCurrentPhotoTypeId] = useState<string | null>(
        null
    );
    const [checklist, setChecklist] = useState([]);
    const [allowFiles, setAllowFiles] =
        useState<FileUploadType>(allowFileUpload);
    const { data: workOrderPhotoTypes, status: workOrderPhotoTypesStatus } =
        useWorkOrderPhotoTypesQuery();

    const metaDataLoaded = useMemo(() => {
        return isMetaDataLoaded([workOrderPhotoTypesStatus]);
    }, [workOrderPhotoTypesStatus]);

    const photoTypeRequirements = useMemo(() => {
        const _clientRequirements = clientRequirements || [];
        const _projectRequirements = projectRequirements || [];
        return [..._clientRequirements, ..._projectRequirements]?.filter(
            requirement => requirement.requirementTypeId === 'PHOTO'
        );
    }, [clientRequirements, projectRequirements]);

    const photoTypeContextValue = useMemo(
        () => ({ currentPhotoTypeId, setCurrentPhotoTypeId }),
        [currentPhotoTypeId, setCurrentPhotoTypeId]
    );

    useEffect(() => {
        if (document?.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const requiredChecklist =
                _.filter(
                    document.requirements,
                    requirement => requirement.requirementTypeId === 'CHECKLIST'
                ) ?? [];
            setChecklist(requiredChecklist);
        }
        setAllowFiles(
            getAllowFileUpload(document?.client?.enabledVideoRecording)
        );
    }, [document]);

    const handleUploadOnClick = async ({
        typeId,
        description,
        requiresPullAndClean,
    }: {
        typeId: string;
        description: string;
        requiresPullAndClean: boolean;
    }) => {
        const file = acceptedFiles[0];
        try {
            await handleUploadPhotosOnClick?.(
                file,
                typeId,
                description,
                requiresPullAndClean
            );
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                acceptedFiles
            );
            setAcceptedFiles([]);
            setCurrentPhotoTypeId(null);
        } catch (error) {
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                acceptedFiles
            );
            setAcceptedFiles([]);
        }
    };

    const handleCancelOnClick = () => {
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setCurrentPhotoTypeId(null);
    };

    const handleCloseOnClick = () => {
        cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
            acceptedFiles
        );
        setAcceptedFiles([]);
        setCurrentPhotoTypeId(null);
    };

    const handleOnDrop = (
        newAcceptedFiles: AcceptedFile[],
        rejectedFiles: File[]
    ) => {
        setAcceptedFiles(newAcceptedFiles);
        if (rejectedFiles.length > 0) {
            Alert.warning(getFileUploadErrorMessage(allowFiles), {
                autoClose: false,
            });
            cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser(
                rejectedFiles
            );
        }
    };

    const handleSaveChecklistOnClick = async (checklist, sections) => {
        try {
            await saveChecklist(checklist, sections);
            const updatedWorkOrder = await getWorkOrder(document.id!);

            dispatch(replace(updatedWorkOrder));

            return Alert.success('Success! Checklist was saved');
        } catch (error) {
            logger.error(error);

            Alert.error(
                'An error occurred saving the checklist. Please contact Motili.'
            );
        }
    };

    const isShowStatusFilter = useMemo(
        () => isStatusFilterAvailable(document?.statusId, photos),
        [photos, document?.statusId]
    );

    const allowUpload = allowUploadPhoto({
        document,
        isMotiliAccount,
        isContractorAccount,
        accountId,
    });

    const getFileDropZone = useCallback(
        () => (
            <FileUploadDropZone
                multiple={false}
                accept={allowFiles}
                disabled={disabled || !allowUpload}
                acls={acls || allowUpload}
                handleOnDrop={handleOnDrop}
                enableNotApplicablePhoto={enableNotApplicablePhoto}
                readOnly={!allowUpload}
            />
        ),
        [
            allowFiles,
            disabled,
            allowUpload,
            acls,
            handleOnDrop,
            enableNotApplicablePhoto,
        ]
    );

    return (
        <Content loading={!metaDataLoaded}>
            <DocumentRequirementsPhotosUploadModalContext.Provider
                value={photoTypeContextValue}
            >
                <DocumentRequirementsPhotosUploadDrawer
                    handleCloseOnClick={handleCancelOnClick}
                    handleCancelOnClick={handleCloseOnClick}
                    handleUploadOnClick={({
                        typeId,
                        description,
                        requiresPullAndClean,
                    }: {
                        typeId?: string;
                        description: string;
                        requiresPullAndClean: boolean;
                    }) =>
                        handleUploadOnClick({
                            typeId: typeId || currentPhotoTypeId!,
                            description,
                            requiresPullAndClean,
                        })
                    }
                    photoTypes={workOrderPhotoTypes || []}
                    currentPhotoTypeId={currentPhotoTypeId}
                    photoTypeRequirements={photoTypeRequirements}
                    photos={photos}
                    fileName={acceptedFiles[0]?.name}
                    imgSrc={acceptedFiles[0]?.preview}
                    mimeType={acceptedFiles[0]?.type}
                    open={acceptedFiles.length > 0}
                    file={acceptedFiles[0]}
                />

                <Flex direction='row' align='stretch' gap='16px'>
                    {showPhotoTypeRequirementsSection && (
                        <div
                            style={{
                                width: '35%',
                                paddingBottom: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <DocumentRequirementsPhotosRequired
                                documentStatusId={document?.statusId}
                                documentTypeId={document?.documentTypeId}
                                photos={photos}
                                photoTypes={workOrderPhotoTypes || []}
                                photoTypeRequirements={photoTypeRequirements}
                                handleEditPhotoRequirements={
                                    handleEditPhotoRequirements
                                }
                            />
                        </div>
                    )}
                    <div style={{ flex: 1 }}>
                        {_.isEmpty(checklist) ? (
                            getFileDropZone()
                        ) : (
                            <AdminWorkOrderRequirementsChecklists
                                checklistOptions={[]}
                                property={document.property}
                                workOrder={document}
                                handleSaveChecklistOnClick={
                                    handleSaveChecklistOnClick
                                }
                                checklists={checklist}
                                accountId={accountId}
                            />
                        )}
                    </div>
                </Flex>

                {!_.isEmpty(checklist) && (
                    <Flex>
                        <div style={{ width: '35%', marginTop: '16px' }}>
                            {getFileDropZone()}
                        </div>
                    </Flex>
                )}
                <FormRow style={{ minWidth: 0, overflow: 'hidden' }}>
                    <FormElement
                        style={{
                            minWidth: 0,
                            width: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        {isLoading ? (
                            <LoadingSpinner size='large' />
                        ) : (
                            <DocumentRequirementsPhotosUploaded
                                photos={photos}
                                photoTypes={workOrderPhotoTypes || []}
                                photoTypeRequirements={photoTypeRequirements}
                                disabled={disabled}
                                acls={acls}
                                removeAcls={removeAcls}
                                handleSavePhotoOnClick={handleSavePhotoOnClick}
                                handleUploadPhotoOnClick={
                                    handleUploadPhotosOnClick
                                }
                                handleReUploadPhotoOnClick={
                                    handleReUploadPhotoOnClick
                                }
                                handleRemovePhotoOnClick={
                                    handleRemovePhotoOnClick
                                }
                                handleEditUploadedPhotoRequirement={
                                    handleEditUploadedPhotoRequirement
                                }
                                handleEditPhotoRequirements={
                                    handleEditPhotoRequirements
                                }
                                handleGetApprovalHistoryPhotoRequirement={
                                    handleGetApprovalHistoryPhotoRequirement
                                }
                                handleSaveApprovalPhotoRequirement={
                                    handleSaveApprovalPhotoRequirement
                                }
                                handleBulkApprovalPhotoRequirement={
                                    handleBulkApprovalPhotoRequirement
                                }
                                handleEditApprovalPhotoRequirement={
                                    handleEditApprovalPhotoRequirement
                                }
                                document={document}
                                assetTypes={assetTypes}
                                isClientEnablePhotoVerification={
                                    isClientEnablePhotoVerification
                                }
                                isShowStatusFilter={isShowStatusFilter}
                            />
                        )}
                    </FormElement>
                </FormRow>
            </DocumentRequirementsPhotosUploadModalContext.Provider>
        </Content>
    );
};

export const allowUploadPhoto = (params: {
    document?: Document;
    isMotiliAccount: boolean;
    isContractorAccount: boolean;
    accountId: number;
}) => {
    const { document, isMotiliAccount, isContractorAccount, accountId } =
        params;

    if (!document) return false;

    if (isMotiliAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const disableStatus = [
                WORK_ORDER_STATUS.COMPLETED,
                WORK_ORDER_STATUS.CLOSED,
                WORK_ORDER_STATUS.CANCELLED,
                WORK_ORDER_STATUS.INITIALIZED,
                WORK_ORDER_STATUS.DRAFT_CANCELLED,
            ];
            return !disableStatus.includes(
                document.statusId as WORK_ORDER_STATUS
            );
        }

        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const disableStatus = [
                QUOTE_STATUS.COMPLETED,
                QUOTE_STATUS.CLOSED,
                QUOTE_STATUS.CANCELLED,
                QUOTE_STATUS.DRAFT_CANCELLED,
            ];
            return !disableStatus.includes(document.statusId as QUOTE_STATUS);
        }
    }
    if (isContractorAccount) {
        if (document.documentTypeId === DOCUMENT_TYPE.WORK_ORDER) {
            const isCheckIn = !!document.checkInsV2?.find(
                checkIn =>
                    !checkIn.checkedOutAt &&
                    checkIn.checkedInUserId === accountId
            );

            const allowStatus = [WORK_ORDER_STATUS.IN_PROCESS];
            return (
                allowStatus.includes(document.statusId as WORK_ORDER_STATUS) &&
                isCheckIn
            );
        }
        if (document.documentTypeId === DOCUMENT_TYPE.QUOTE) {
            const allowStatus = [QUOTE_STATUS.REVISING];
            return allowStatus.includes(document.statusId as QUOTE_STATUS);
        }
    }
    return false;
};

export const cleanUpFilesSoWeDontCauseAMemoryLeakAndCrashSomeonesBrowser = (
    files: (File | AcceptedFile)[]
) => {
    try {
        files.forEach(file => {
            if ((file as AcceptedFile).preview) {
                window.URL.revokeObjectURL((file as AcceptedFile).preview!);
            }
        });
    } catch (error) {
        console.error(error);
    }
};

interface DocumentRequirementsPhotosUploadModalContextType {
    currentPhotoTypeId: string | null;
    setCurrentPhotoTypeId: (value: string | null) => void;
}

export const DocumentRequirementsPhotosUploadModalContext =
    createContext<DocumentRequirementsPhotosUploadModalContextType>({
        currentPhotoTypeId: null,
        setCurrentPhotoTypeId: () => {},
    });

const DocumentRequirementsPhotosWrapper = (
    props: DocumentRequirementsPhotosProps
) => {
    const { isLegacyUI } = useUI();
    return isLegacyUI ? (
        <LegacyDocumentRequirementsPhotos {...props} />
    ) : (
        <DocumentRequirementsPhotos {...props} />
    );
};

export const isStatusFilterAvailable = (workOrderStatusId, mediaList) =>
    _.some(mediaList, media => !_.isNil(media.approvalStatusId));

export default DocumentRequirementsPhotosWrapper;
